"use client";

import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, AlertTriangle, ShieldAlert, X } from "lucide-react";
import {
  type ActivePlatformAnnouncement,
  dismissPlatformAnnouncement,
  getActivePlatformAnnouncements,
} from "@/app/_module/services/platform-announcements";
import { useAnnouncementLiveRefresh } from "@/app/_module/hooks/useAnnouncementLiveRefresh";

const QUERY_KEY = ["platform-announcements", "active"] as const;
const POLL_MS = 45 * 1000; // safety-net poll; live changes + tab focus refetch immediately (useAnnouncementLiveRefresh)

function severityStyles(severity: ActivePlatformAnnouncement["severity"]) {
  switch (severity) {
    case "critical":
      return {
        bg: "bg-red-50",
        border: "border-red-500",
        text: "text-red-800",
        iconColor: "text-red-600",
        icon: ShieldAlert,
        urgencyLabel: "CRITICAL",
        urgencyClass: "bg-red-600 text-white",
      };
    case "warning":
      return {
        bg: "bg-amber-50",
        border: "border-amber-500",
        text: "text-amber-900",
        iconColor: "text-amber-600",
        icon: AlertTriangle,
        urgencyLabel: "IMPORTANT",
        urgencyClass: "bg-amber-600 text-white",
      };
    default:
      return {
        bg: "bg-blue-50",
        border: "border-blue-500",
        text: "text-blue-900",
        iconColor: "text-blue-600",
        icon: Info,
        urgencyLabel: null,
        urgencyClass: "",
      };
  }
}

function isLikelyExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export default function PlatformAnnouncementBanner() {
  const queryClient = useQueryClient();
  const [optimisticallyDismissed, setOptimisticallyDismissed] = useState<Set<string>>(new Set());

  const { data: announcements = [], refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getActivePlatformAnnouncements,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: POLL_MS / 2,
    // Don't retry aggressively — banner is non-critical UI.
    retry: 1,
  });
  // A critical announcement must show up while the agent is mid-session, not
  // on their next page load.
  useAnnouncementLiveRefresh(() => { void refetch(); });

  const visible = announcements.filter((a) => !optimisticallyDismissed.has(a.id));
  if (visible.length === 0) return null;

  const handleDismiss = async (id: string) => {
    setOptimisticallyDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    await dismissPlatformAnnouncement(id);
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };

  return (
    <div className="space-y-2 px-4 pt-3" role="region" aria-label="Platform announcements">
      {visible.map((a) => {
        const s = severityStyles(a.severity);
        const Icon = s.icon;
        return (
          <div
            key={a.id}
            className={`${s.bg} border-l-4 ${s.border} p-3 rounded-r-md shadow-sm`}
            role={a.severity === "critical" ? "alert" : "status"}
            aria-live={a.severity === "critical" ? "assertive" : "polite"}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start flex-1 min-w-0 gap-3">
                <Icon className={`h-5 w-5 ${s.iconColor} flex-shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <h3 className={`text-sm font-semibold ${s.text}`}>{a.title}</h3>
                    {s.urgencyLabel && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${s.urgencyClass}`}>
                        {s.urgencyLabel}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${s.text} whitespace-pre-wrap`}>{a.body}</p>
                  {a.ctaUrl && a.ctaLabel && (
                    <a
                      href={a.ctaUrl}
                      target={isLikelyExternalUrl(a.ctaUrl) ? "_blank" : undefined}
                      rel={isLikelyExternalUrl(a.ctaUrl) ? "noopener noreferrer" : undefined}
                      className={`inline-block mt-2 text-sm font-medium underline ${s.text}`}
                    >
                      {a.ctaLabel} →
                    </a>
                  )}
                </div>
              </div>
              {a.isDismissible && (
                <button
                  type="button"
                  onClick={() => handleDismiss(a.id)}
                  className={`${s.text} hover:opacity-70 p-1 rounded shrink-0`}
                  aria-label="Dismiss announcement"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
